import { cn } from '@parcel/ui';
import * as React from 'react';
import {
  buildExcerptWindow,
  buildHighlightSegments,
  chooseHaystack,
} from '../../lib/mention-highlight.js';
import { type BadgeVariant, MENTION_MARK_CLASSES } from '../../lib/mentions.js';

interface MentionSectionReaderProps {
  sectionText: string | null;
  excerpt: string | null;
  variant: BadgeVariant;
}

export function MentionSectionReader({ sectionText, excerpt, variant }: MentionSectionReaderProps) {
  const haystack = chooseHaystack(sectionText, excerpt);
  const [expanded, setExpanded] = React.useState(false);
  const [overflows, setOverflows] = React.useState(false);
  const textRef = React.useRef<HTMLParagraphElement>(null);
  const textId = React.useId();

  // The three-line clamp can hide text the window kept, so the toggle also follows real overflow.
  // biome-ignore lint/correctness/useExhaustiveDependencies: new text re-measures the clamp
  React.useEffect(() => {
    const node = textRef.current;
    if (!node || expanded) return;
    const measure = () => setOverflows(node.scrollHeight > node.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [expanded, haystack, excerpt]);

  if (!haystack) {
    return (
      <p className="text-[13px] italic text-muted-foreground">
        No quoted text available for this mention.
      </p>
    );
  }

  const excerptWindow = buildExcerptWindow(haystack, excerpt);
  const clipped = excerptWindow.clippedStart || excerptWindow.clippedEnd;
  // Collapsed, the window reads as one quoted run; expanded keeps the section's own line breaks.
  const visibleText = expanded ? haystack : excerptWindow.text.replace(/\s+/g, ' ').trim();
  const segments = buildHighlightSegments(visibleText, excerpt ?? '');

  return (
    <div>
      <p
        id={textId}
        ref={textRef}
        className={cn(
          'text-[13px] leading-5 text-muted-foreground',
          expanded ? 'whitespace-pre-wrap' : 'line-clamp-3',
        )}
      >
        {!expanded && excerptWindow.clippedStart ? (
          <span className="text-muted-foreground">… </span>
        ) : null}
        {segments.map((seg, i) =>
          seg.highlighted ? (
            <mark
              key={`${i}-${seg.text.slice(0, 12)}`}
              className={cn('rounded px-0.5 ring-1', MENTION_MARK_CLASSES[variant])}
            >
              {seg.text}
            </mark>
          ) : (
            <span key={`${i}-${seg.text.slice(0, 12)}`}>{seg.text}</span>
          ),
        )}
        {!expanded && excerptWindow.clippedEnd ? (
          <span className="text-muted-foreground"> …</span>
        ) : null}
      </p>
      {clipped || overflows || expanded ? (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={textId}
          className="mt-1 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show less' : clipped ? 'Show full section' : 'Show more'}
        </button>
      ) : null}
    </div>
  );
}
